import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { isEqual } from 'lodash';
import { useDebounce } from 'use-debounce';
import { useTheme } from 'styled-components';
import type Highcharts from 'highcharts';
import { Box, Button, Flex, SegmentedControl, Select, Text } from 'core/components';
import { formatMetricValue } from 'app/views/ude/dataviews/util/unitFormatter';
import { buildMetricCharts, TestLike } from './buildMetricQueries';
import CollapsibleChart from './CollapsibleChart';
import SynthFilterBar from '../../filters/SynthFilterBar';
import { TAB_FILTERS } from '../../filters/tabFilterConfig';

interface SynthUdeMetricsPanelProps {
  test: TestLike;
}

const CHART_OVERRIDES = {
  yAxis: { title: { text: '' } },
  plotOptions: {
    series: {
      // Perf: don't render a marker per data point (big win at high point counts);
      // keep a marker on hover so the tooltip still highlights the point.
      marker: { enabled: false, states: { hover: { enabled: true } } },
      // Allow dense series without Highcharts' turbo point-count cap (matches other synth charts).
      turboThreshold: 0
    }
  },
  // Keep the tooltip inside the chart SVG. The base UDE config uses `outside: true`, which
  // renders the shared tooltip in a document.body div; near the viewport edge it toggles a
  // page scrollbar and reflows every full-width chart ("jumpy" on hover).
  tooltip: { outside: false }
};

const CHART_SIZE_OPTIONS = [
  { label: 'Normal', value: 'normal' },
  { label: 'Compact', value: 'compact' }
];

// Fixed legend presentation: right-aligned, table-style legend with rollups.
const CHART_SETTINGS = {
  chart: { legend: { position: 'right' as const, mode: 'table' as const, showRollups: true } }
};

// Maps metric names to their healthSettings key prefix
const METRIC_TO_HEALTH_KEY: Record<string, string> = {
  ping_latency_avg: 'latency',
  ping_jitter_avg: 'jitter',
  ping_packet_loss_pct: 'packetLoss',
  dns_latency_avg: 'dnsLatency',
  http_latency_avg: 'httpLatency',
  pageload_latency_avg: 'httpLatency',
  http_status_code: 'httpStatusCode'
};

// Measurement unit per metric, so threshold labels render in the same unit as the chart axis
// (latency/jitter thresholds are stored in microseconds; packet loss is a percent).
const METRIC_TO_UNIT: Record<string, string> = {
  ping_latency_avg: 'duration:microseconds',
  ping_jitter_avg: 'duration:microseconds',
  ping_packet_loss_pct: 'percent',
  dns_latency_avg: 'duration:microseconds',
  http_latency_avg: 'duration:microseconds',
  pageload_latency_avg: 'duration:microseconds'
};

interface HealthColors {
  warning: string;
  danger: string;
}

function getHealthPlotLines(
  test: TestLike,
  metricName: string,
  colors: HealthColors
): Highcharts.YAxisPlotLinesOptions[] {
  const healthKey = METRIC_TO_HEALTH_KEY[metricName];
  if (!healthKey) {
    return [];
  }

  const unit = METRIC_TO_UNIT[metricName];
  const healthSettings = (test.get('config.healthSettings') || {}) as Record<string, number>;
  const warning = healthSettings[`${healthKey}Warning`];
  const critical = healthSettings[`${healthKey}Critical`];
  const lines: Highcharts.YAxisPlotLinesOptions[] = [];

  if (warning && warning > 0) {
    lines.push({
      value: warning,
      color: colors.warning,
      dashStyle: 'Dash',
      width: 1,
      zIndex: 3,
      label: {
        text: `Warning: ${formatMetricValue(warning, unit).displayFull}`,
        style: { color: colors.warning, fontSize: '10px' }
      }
    });
  }
  if (critical && critical > 0) {
    lines.push({
      value: critical,
      color: colors.danger,
      dashStyle: 'Dash',
      width: 1,
      zIndex: 3,
      label: {
        text: `Critical: ${formatMetricValue(critical, unit).displayFull}`,
        style: { color: colors.danger, fontSize: '10px' }
      }
    });
  }
  return lines;
}

const VIZ_LIMIT_OPTIONS = [
  { label: '5', value: 5 },
  { label: '10', value: 10 },
  { label: '15', value: 15 },
  { label: '20', value: 20 },
  { label: '25', value: 25 },
  { label: '30', value: 30 },
  { label: '40', value: 40 }
];

const SynthUdeMetricsPanel = observer(({ test }: SynthUdeMetricsPanelProps) => {
  const { results } = test;
  const { colors } = useTheme() as { colors: HealthColors };
  const [vizLimit, setVizLimit] = useState(10);
  const [chartSize, setChartSize] = useState<'normal' | 'compact'>('normal');

  const chartHeight = chartSize === 'compact' ? 275 : 400;

  // Debounce filter values so rapid multi-select clicks fire a single query (~1s after last change).
  // Compare by content (isEqual): the shared-filter getters return a fresh array each render, which
  // would otherwise defeat use-debounce's change detection and cause a self-retriggering rebuild loop.
  const [debouncedAgentIds] = useDebounce(results.sharedFilterAgentIds.slice(), 1000, { equalityFn: isEqual });
  const [debouncedTargets] = useDebounce(results.sharedFilterTargets.slice(), 1000, { equalityFn: isEqual });
  const [debouncedLabelIds] = useDebounce(results.sharedFilterLabelIds.slice(), 1000, { equalityFn: isEqual });
  const [debouncedSiteIds] = useDebounce(results.sharedFilterSiteIds.slice(), 1000, { equalityFn: isEqual });
  const [debouncedAsns] = useDebounce(results.sharedFilterAsns.slice(), 1000, { equalityFn: isEqual });

  const hasFilters = results.filters.isActive;
  const handleClearFilters = () => results.resetSharedFilters();

  const charts = useMemo(
    () =>
      buildMetricCharts(
        test,
        { start: results.actualStartDate, end: results.actualEndDate },
        {
          agentIds: debouncedAgentIds,
          targets: debouncedTargets,
          labelIds: debouncedLabelIds,
          siteIds: debouncedSiteIds,
          asns: debouncedAsns
        },
        vizLimit
      ),
    [
      test,
      results.actualStartDate,
      results.actualEndDate,
      debouncedAgentIds,
      debouncedTargets,
      debouncedLabelIds,
      debouncedSiteIds,
      debouncedAsns,
      vizLimit
    ]
  );

  return (
    <Flex flexDirection="column" gap={3} flex={1} p={2}>
      {/* Filters fill the row; series depth + chart size are right-aligned in the same row */}
      <SynthFilterBar
        test={test}
        filters={TAB_FILTERS.metrics}
        title="Filter Metrics"
        trailingControls={
          <Flex ml="auto" alignItems="flex-start" gap="12px">
            {hasFilters && (
              <Button small minimal intent="warning" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            )}
            <Box>
              <Text as="div" small muted mb="4px" fontWeight="bold">
                Series Depth
              </Text>
              <Select menuWidth={80} options={VIZ_LIMIT_OPTIONS} values={vizLimit} onChange={setVizLimit} />
            </Box>
            <Box>
              <Text as="div" small muted mb="4px" fontWeight="bold">
                Chart Size
              </Text>
              <SegmentedControl
                options={CHART_SIZE_OPTIONS}
                value={chartSize}
                onChange={setChartSize as (v: string) => void}
              />
            </Box>
          </Flex>
        }
      />
      {charts.map(({ key, title, query }) => {
        const metricName = query.metrics[0]?.name || '';
        const plotLines = getHealthPlotLines(test, metricName, colors);
        const overrides =
          plotLines.length > 0
            ? { ...CHART_OVERRIDES, yAxis: { ...CHART_OVERRIDES.yAxis, plotLines } }
            : CHART_OVERRIDES;

        return (
          <CollapsibleChart
            key={key}
            title={title}
            query={query}
            chartHeight={chartHeight}
            chartOverrides={overrides}
            settings={CHART_SETTINGS}
          />
        );
      })}
    </Flex>
  );
});

export default SynthUdeMetricsPanel;
